<script>
  import shortid from 'shortid';
  import { onDestroy, onMount } from 'svelte';
  import Dialog from '../../Common/Dialog/Dialog.svelte';
  import PulseStatusAnim from '../../Common/PulseStatusAnim.svelte';
  import Controls from './Controls.svelte';
  import MediaDevices from './MediaDevices.svelte';
  import CameraPreview from './CameraPreview.svelte';
  import { broadcaster, videoPlayer } from './broadcast-store';
  import { fade } from 'svelte/transition';
  import { applicationConfig, user } from '../../../Store/store';
  import { listenToChannel } from '../../../Api/api';
  import { calcVideoDrawSize } from './CanvasRenderer/video-utils';

  export let channelId = '';

  let player;
  $: channel = null;
  let unsubscribeListenToChannel;
  let guestContainer;
  let deviceSelectorVisible = false;

  let addedGuestStreamIds = [];
  let addedGuestStreams = {};

  $: channelId && listenToChannelData(channelId);
  $: $broadcaster.guestStreamInfos.length && guestContainer && addGuestPreviewPlayers();

  $: mediaConstraints = {
    video: {
      width: {
        ideal: 1280,
      },
      height: {
        ideal: 720,
      },
      facingMode: $broadcaster.facingMode,
    },
    audio: { echoCancellation: true },
  };

  const addGuestPreviewPlayers = () => {
    if (guestContainer) {
      $broadcaster.guestStreamInfos.forEach((gsi) => {
        guestContainer.appendChild(gsi.video);
      });
    }
  };

  onMount(() => {
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
  });

  onDestroy(() => {
    if (guestContainer) {
      guestContainer.innerHTML = '';
    }
    navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
      unsubscribeListenToChannel = null;
    }
  });

  const listenToChannelData = async (channelId) => {
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
      unsubscribeListenToChannel = null;
    }
    unsubscribeListenToChannel = await listenToChannel(channelId, (docRef) => {
      if (docRef.exists) {
        const c = { ...docRef.data(), id: docRef.id };
        if (!c.roomId && $broadcaster.streamId) {
          broadcaster.stopStreams();
        }
        channel = c;
      }
    });
  };

  const init = async () => {
    broadcaster.update((s) => ({ ...s, userMediaInitializing: true }));

    const userMediaStream = await navigator.mediaDevices.getUserMedia(mediaConstraints);

    const devices = await navigator.mediaDevices.enumerateDevices();
    broadcaster.setDevices(devices);

    broadcaster.setCameraStream(userMediaStream);
  };

  const getDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    broadcaster.setDevices(devices);
  };

  const onCamSelected = async (e) => {
    const newMediaContraints = {
      video: { ...mediaConstraints.video, deviceId: e.detail.deviceId },
    };
    const s = await navigator.mediaDevices.getUserMedia(newMediaContraints);
    if ($broadcaster.streamingPreviewToGuests) {
      broadcaster.updateGuestStream(s);
    }
    broadcaster.setCameraStream(s);
  };

  const onMicSelected = async (e) => {
    const newMediaContraints = {
      audio: { ...mediaConstraints.audio, deviceId: e.detail.deviceId },
    };
    const s = await navigator.mediaDevices.getUserMedia(newMediaContraints);
    broadcaster.replaceAudio(s);
    broadcaster.setActiveDevices(s);
  };

  const onInitUserMedia = () => {
    init();
  };

  const onCloseCamAndMic = () => {
    broadcaster.closeStream();
  };

  const onFlipCamera = async () => {
    const facingMode = $broadcaster.facingMode === 'user' ? 'environment' : 'user';
    broadcaster.update((s) => ({
      ...s,
      flippingCam: true,
      facingMode,
    }));

    // Needed for Android
    // isAndroid -> https://davidwalsh.name/detect-android
    var ua = navigator.userAgent.toLowerCase();
    var isAndroid = ua.indexOf('android') > -1;
    if (isAndroid) {
      // Samsung android requires that we set the video el src to null, or browser freezes
      player.clearStream();
      // All androids require that we stop the video track
      $broadcaster.userMediaStream.getVideoTracks().forEach((track) => {
        track.stop();
      });
    }

    const newMediaContraints = {
      video: { ...mediaConstraints.video, facingMode: { exact: facingMode } },
    };

    const s = await navigator.mediaDevices.getUserMedia(newMediaContraints);
    broadcaster.setCameraStream(s);

    broadcaster.update((s) => ({
      ...s,
      flippingCam: false,
    }));
  };

  const onCloseDeviceSelector = () => {
    deviceSelectorVisible = false;
  };

  const getUserName = () => {
    return $user.displayName ? $user.displayName : $user.email;
  };

  const onPublish = () => {
    if (channel) {
      const streamer = getUserName();
      const room = shortid.generate();
      broadcaster.publish(
        channel,
        streamer,
        $broadcaster.stream,
        $applicationConfig.publishURL,
        room,
      );
    }
  };

  const onUnpublish = () => {
    addedGuestStreamIds = [];
    addedGuestStreams = {};
    broadcaster.stopStreams();
  };

  const onActivateGuestMode = () => {
    broadcaster.update((s) => ({ ...s, joinAsGuest: true }));
    init();
  };

  const onJoinRoom = () => {
    const streamer = getUserName();
    broadcaster.join(
      channel,
      streamer,
      $broadcaster.stream,
      $applicationConfig.publishURL,
      channel.room,
    );
  };

  const onLeaveRoom = () => {
    broadcaster.leaveRoom(channel);
  };
</script>

<div class="reporter-container" in:fade>
  {#if !$broadcaster.joinAsGuest && channel?.roomId && !$broadcaster.isBroadcasting}
    <div class="broadcast-info">
      <h1 class="info">
        {channel.streamer} er live på dette chat-rommet.
      </h1>
      <button on:click={onActivateGuestMode} class="join-chat-btn">Bli med som gjest</button>
      <button class="stream-kill-switch" on:click={broadcaster.removeStream(channel?.id)}>
        <span class="material-icons"> cancel_presentation </span>
        Steng sending
      </button>
    </div>
  {:else if $broadcaster.isBroadcasting && channel?.id !== $broadcaster.channelId}
    <h1 class="info">Du er live på {$broadcaster.channel?.name}</h1>
  {:else}
    <div class="video-preview">
      <div class="guests-container" bind:this={guestContainer} />
      <div class="player-container">
        <CameraPreview bind:this={player} />
      </div>
      {#if $broadcaster.videoCanPlay}
        <button
          on:click={onCloseCamAndMic}
          class="close-all-btn"
          disabled={$broadcaster.isBroadcasting ||
            $broadcaster.isBroadcastingAsGuest ||
            $broadcaster.isInitializingPublish}
        >
          <span class="material-icons"> cancel </span>
        </button>
        <p class="playout-type">{channel?.playout ? channel.playout : 'HLS'}</p>
      {/if}
    </div>

    {#if $broadcaster.videoCanPlay && channel}
      <div class="cam-on-content">
        <Controls
          joinAsGuest={$broadcaster.joinAsGuest && channel.roomId}
          on:joinAsGuest={onJoinRoom}
          on:leaveAsGuest={onLeaveRoom}
          on:publish={onPublish}
          on:unpublish={onUnpublish}
          on:opensettings={() => (deviceSelectorVisible = true)}
          on:flipcam={onFlipCamera}
        />
      </div>
    {:else}
      <div class="cam-off-content">
        {#if ($broadcaster.userMediaInitialized || $broadcaster.userMediaInitializing) && !$broadcaster.videoCanPlay}
          <div class="status">
            <PulseStatusAnim />
          </div>
        {:else}
          <button on:click={onInitUserMedia} class="cam-on-btn">Slå på kamera</button>
        {/if}
      </div>
    {/if}
  {/if}
</div>

<Dialog
  open={deviceSelectorVisible}
  on:cancel={onCloseDeviceSelector}
  title="Velg kamera og mikrofon"
>
  <div slot="content">
    <MediaDevices on:camselected={onCamSelected} on:micselected={onMicSelected} />
  </div>
</Dialog>

<style src="./broadcast.scss"></style>
